import { getLatestEditorRelease, GitHubRelease, isNewerEditorRelease, LayoutMode } from '@lved/shared-core';
import { isBrowserOnlyTheia } from '@theia-lved-core/browser/online-preview/environment';
import type { AppShell } from '@theia-lved-core/browser/shell/application-shell';
import { codicon, ApplicationShell as TheiaApplicationShell } from '@theia/core/lib/browser';
import { CommandService } from '@theia/core/lib/common/command';
import { MessageService } from '@theia/core/lib/common/message-service';
import { Widget } from '@theia/core/lib/browser/widgets';
import { inject, injectable, postConstruct } from '@theia/core/shared/inversify';

@injectable()
export class TopActionsWidget extends Widget {
    static readonly ID = 'widget:lvgl-top-actions';

    @inject(CommandService)
    protected readonly commandService!: CommandService;

    @inject(MessageService)
    protected readonly messageService!: MessageService;

    @inject(TheiaApplicationShell)
    protected readonly shell!: TheiaApplicationShell;

    protected content!: HTMLDivElement;
    protected licenseLabel!: HTMLSpanElement;
    protected updateLabel!: HTMLSpanElement;
    protected codeButton!: HTMLButtonElement;
    protected designButton?: HTMLButtonElement;
    protected offlineBadge!: HTMLDivElement;
    protected offlineLabel!: HTMLSpanElement;
    protected offlineDetail!: HTMLSpanElement;

    protected layoutModeUnsubscribe?: () => void;
    protected licenseUnsubscribe?: () => void;
    protected nonCommercialLicenseUnsubscribe?: () => void;
    protected networkStatusHandler?: () => void;
    protected lastGraceNotificationUntil?: number;
    protected lastGraceExpiredNotificationUntil?: number;

    constructor() {
        super({ node: document.createElement('div') });
    }

    @postConstruct()
    protected init(): void {
        this.id = TopActionsWidget.ID;
        this.node.id = 'lvgl-top-actions';
        this.title.closable = false;
        this.node.style.overflow = 'visible';
        this.buildDom();
        this.bootstrapStores();
    }

    override dispose(): void {
        this.layoutModeUnsubscribe?.();
        this.licenseUnsubscribe?.();
        this.nonCommercialLicenseUnsubscribe?.();
        if (this.networkStatusHandler) {
            window.removeEventListener('online', this.networkStatusHandler);
            window.removeEventListener('offline', this.networkStatusHandler);
            this.networkStatusHandler = undefined;
        }
        super.dispose();
    }

    protected getTitlebarControlsWidth(): number {
        const platform = window?.platform?.getHostPlatform?.() ?? '';
        if (platform === 'win32') return 138;
        if (platform === 'linux') return 108;
        return 0;
    }

    protected getRightInset(): string {
        return `calc(${this.getTitlebarControlsWidth()}px + 0.75rem)`;
    }

    protected buildDom(): void {
        const content: HTMLDivElement = document.createElement('div');
        content.className = 'lvgl-top-actions-content';
        content.style.right = this.getRightInset();

        const segment: HTMLElement = document.createElement('div');
        segment.className = 'lvgl-layout-segment';

        this.codeButton = this.createTabButton('Code', 'edit-code');
        this.codeButton.classList.add('lvgl-layout-segment-item');
        this.codeButton.addEventListener('click', () => void this.setLayoutMode(LayoutMode.CODE));

        if (!isBrowserOnlyTheia()) {
            this.designButton = this.createTabButton('Design', 'cursor');
            this.designButton.classList.add('lvgl-layout-segment-item');
            this.designButton.addEventListener('click', () => void this.setLayoutMode(LayoutMode.DESIGN));
            segment.append(this.codeButton, this.designButton);
        } else {
            segment.append(this.codeButton);
        }

        this.offlineBadge = document.createElement('div');
        this.offlineBadge.className = 'lvgl-top-actions-offline-badge';
        this.offlineBadge.title = 'You are offline';

        const offlineIconHost: HTMLSpanElement = document.createElement('span');
        offlineIconHost.innerHTML =
            '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M12.4199 2.52452C12.6822 2.20455 13.1542 2.15728 13.4746 2.41905C13.795 2.68121 13.8419 3.15317 13.5801 3.47374L11.9893 5.4171C12.1007 5.63011 12.1949 5.85371 12.2715 6.08507C13.833 6.43638 14.9996 7.83161 15 9.49913C15 11.2623 13.696 12.7203 12 12.963V12.9991H5.78613L4.58008 14.4737C4.31778 14.7943 3.84597 14.8415 3.52539 14.5792C3.21975 14.3288 3.1623 13.8877 3.38574 13.5694L11.0469 4.18858C11.0489 4.19045 11.0507 4.19257 11.0527 4.19444L12.4199 2.52452ZM8 2.99913C8.63265 2.99913 9.23423 3.13139 9.78027 3.3673L2.33105 12.4894C1.5292 11.9509 1 11.0376 1 9.99913C1.00042 8.50485 2.09335 7.26549 3.52344 7.03722C3.75512 4.76935 5.67096 2.99913 8 2.99913Z" fill="currentColor"/>' +
            '</svg>';
        const offlineIcon: Element | null = offlineIconHost.firstElementChild;
        if (offlineIcon) this.offlineBadge.appendChild(offlineIcon);

        this.offlineLabel = document.createElement('span');
        this.offlineLabel.className = 'lvgl-top-actions-offline-badge-label';
        this.offlineLabel.textContent = 'Offline';
        this.offlineBadge.appendChild(this.offlineLabel);

        this.offlineDetail = document.createElement('span');
        this.offlineDetail.className = 'lvgl-top-actions-offline-badge-detail';
        this.offlineBadge.appendChild(this.offlineDetail);

        this.licenseLabel = document.createElement('span');
        this.licenseLabel.className = 'lvgl-top-actions-license-label';
        this.licenseLabel.textContent = 'Non-Commercial Use Only';

        if (!isBrowserOnlyTheia()) {
            // We're going to check for updates asynchronously.
            // We don't want to block the rest of the UI from rendering while we do so.
            this.checkForUpdate()
                .then(([updateAvailable, latestRelease]: [boolean, GitHubRelease | null]) => {
                    if (updateAvailable) {
                        this.updateLabel = document.createElement('button');
                        this.updateLabel.innerHTML = 'Update';
                        this.updateLabel.className = 'lvgl-top-actions-update-label';
                        this.updateLabel.onclick = () =>
                            void this.commandService.executeCommand('lvgl.topActions.update', latestRelease);
                        // Put this button between the license label on the left, and everything else to the right
                        segment.prepend(this.updateLabel);
                    }
                })
                .catch((error: unknown) => {
                    console.error(`[TopActionsWidget] Failed to check for editor updates: ${String(error)}`);
                });
        }

        content.append(this.licenseLabel, segment, this.offlineBadge);
        this.node.appendChild(content);
        this.content = content;
    }

    protected async checkForUpdate(): Promise<[boolean, GitHubRelease | null]> {
        try {
            const latestRelease: GitHubRelease | null = await getLatestEditorRelease();
            const updateAvailable: boolean = isNewerEditorRelease(latestRelease, window.LVGL_EDITOR_VERSION);
            return [updateAvailable, latestRelease];
        } catch (error: unknown) {
            console.error(`[TopActionsWidget] Failed to check for editor updates: ${String(error)}`);
        }

        return [false, null];
    }

    protected createCodicon(name: string): HTMLSpanElement {
        const span: HTMLSpanElement = document.createElement('span');
        span.className = codicon(name);
        return span;
    }

    protected createTabButton(label?: string, icon?: string): HTMLButtonElement {
        const button: HTMLButtonElement = document.createElement('button');
        button.type = 'button';
        button.className = 'theia-tabbar-tab lvgl-top-actions-tab';
        if (icon) {
            const iconEl: HTMLSpanElement = this.createCodicon(icon);
            iconEl.classList.add('lvgl-top-actions-tab-icon');
            button.appendChild(iconEl);
        }
        if (label) {
            const text: HTMLSpanElement = document.createElement('span');
            text.className = 'lvgl-top-actions-tab-label';
            text.textContent = label;
            button.appendChild(text);
        }
        return button;
    }

    protected async setLayoutMode(layoutMode: LayoutMode): Promise<void> {
        try {
            const { useUIStore } = await import('@lved/ui');
            // setLayoutMode may reject the switch (e.g. runtime still loading, unsaved
            // tabs, project operation in progress) — or apply it asynchronously, after
            // any unsaved tabs finish saving. Don't sync shell DOM state here: the
            // layoutModeUnsubscribe subscription (see bootstrapStores) reacts to the
            // store's actual layoutMode and calls syncShellLayout whenever it lands.
            useUIStore.getState().setLayoutMode(layoutMode);
        } catch {
            // Fall back to command execution if store import fails.
            const commandId: string =
                layoutMode === LayoutMode.DESIGN ? 'lvgl.topActions.layout.design' : 'lvgl.topActions.layout.code';
            void this.commandService.executeCommand(commandId);
            this.syncShellLayout(layoutMode);
        }
    }

    protected syncShellLayout(layoutMode: LayoutMode): void {
        const lvglShell: AppShell = this.shell as AppShell;
        const isDesign: boolean = layoutMode === LayoutMode.DESIGN;
        lvglShell.setMainPanelVisible(!isDesign);
        document.body.setAttribute('data-lvgl-main', isDesign ? 'hidden' : 'visible');
        document.body.setAttribute('data-lvgl-layout', layoutMode);
    }

    protected async bootstrapStores(): Promise<void> {
        this.applyOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
        this.networkStatusHandler = () => this.applyOffline(!navigator.onLine);
        window.addEventListener('online', this.networkStatusHandler, { passive: true });
        window.addEventListener('offline', this.networkStatusHandler, { passive: true });

        try {
            const { useUIStore, useLicenseStore, hasNonCommercialLicense } = await import('@lved/ui');
            this.applyLayoutMode(useUIStore.getState().layoutMode);
            this.layoutModeUnsubscribe = useUIStore.subscribe((state) => this.applyLayoutMode(state.layoutMode));

            this.applyNonCommercialLabel(hasNonCommercialLicense(useLicenseStore.getState()));
            this.nonCommercialLicenseUnsubscribe = useLicenseStore.subscribe(
                hasNonCommercialLicense,
                (nonCommercialLicense) => this.applyNonCommercialLabel(nonCommercialLicense),
                { fireImmediately: true }
            );

            this.applyLicenseStatus(
                !!useLicenseStore.getState().offline,
                useLicenseStore.getState().inGracePeriodUntil
            );
            this.licenseUnsubscribe = useLicenseStore.subscribe(
                (state) => ({ offline: state.offline, inGracePeriodUntil: state.inGracePeriodUntil }),
                ({ offline, inGracePeriodUntil }) => this.applyLicenseStatus(!!offline, inGracePeriodUntil),
                { fireImmediately: true }
            );
        } catch {
            this.content.style.visibility = 'hidden';
        }
    }

    // Reacts to the store's actual layoutMode — shell changes are owned by
    // LayoutModeContribution so its transition can cover the complete update.
    protected applyLayoutMode(layoutMode: LayoutMode): void {
        const isCode: boolean = layoutMode === LayoutMode.CODE;
        const isDesign: boolean = layoutMode === LayoutMode.DESIGN;
        this.codeButton.classList.toggle('p-mod-current', isCode);
        this.designButton?.classList.toggle('p-mod-current', isDesign);
    }

    protected applyOffline(offline: boolean, inGracePeriodUntil?: number): void {
        const networkOffline: boolean = typeof navigator !== 'undefined' ? !navigator.onLine : false;
        const showBadge: boolean = offline || networkOffline;
        this.offlineBadge.classList.toggle('lvgl-top-actions-offline-badge-visible', showBadge);

        if (!showBadge) {
            this.offlineDetail.textContent = '';
            this.offlineBadge.title = 'You are offline';
            return;
        }

        if (inGracePeriodUntil && Date.now() <= inGracePeriodUntil) {
            const deadline: string = this.formatGraceDeadline(inGracePeriodUntil);
            this.offlineDetail.textContent = `Reconnect by ${deadline}`;
            this.offlineBadge.title = `You are offline. Reconnect by ${deadline} to keep using LVGL Pro.`;
            return;
        }

        if (inGracePeriodUntil && Date.now() > inGracePeriodUntil) {
            this.offlineDetail.textContent = 'Grace period expired';
            this.offlineBadge.title = 'Offline grace period expired. Reconnect to continue using LVGL Pro.';
            return;
        }

        this.offlineDetail.textContent = 'No connection';
        this.offlineBadge.title = 'You are offline';
    }

    protected applyLicenseStatus(offline: boolean, inGracePeriodUntil?: number): void {
        this.applyOffline(offline, inGracePeriodUntil);
        if (!offline) {
            this.lastGraceNotificationUntil = undefined;
            this.lastGraceExpiredNotificationUntil = undefined;
            return;
        }
        if (!inGracePeriodUntil || this.lastGraceNotificationUntil === inGracePeriodUntil) {
            return;
        }
        this.lastGraceNotificationUntil = inGracePeriodUntil;
        if (Date.now() > inGracePeriodUntil) {
            if (this.lastGraceExpiredNotificationUntil === inGracePeriodUntil) return;
            this.lastGraceExpiredNotificationUntil = inGracePeriodUntil;
            this.messageService.error('Offline grace period expired. Reconnect to continue using LVGL Pro.');
            return;
        }
        this.messageService.warn(
            `You are offline. Reconnect by ${this.formatGraceDeadline(inGracePeriodUntil)} to keep using LVGL Pro.`
        );
    }

    protected applyNonCommercialLabel(nonCommercialLicense: boolean): void {
        this.licenseLabel.classList.toggle('lvgl-top-actions-license-label-visible', nonCommercialLicense);
    }

    protected formatGraceDeadline(timestampMs: number): string {
        return new Date(timestampMs).toLocaleString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour12: false
        });
    }
}
